import { faChartLine, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef } from 'react';

const MAX_VISIBLE_INDEXES = 3;

/**
 * props:
 * - data
 * - onEditClick
 * - onCopyClick
 * - onViewClick
 * - onStopClick
 * - onStartClick
 * - onDeleteClick
 */
function AutomationRow(props) {
    const indexesRef = useRef(null);

    // Bootstrap tooltips are opt-in and volt.js only initializes the ones
    // present on page load, so React-rendered ones need their own init.
    useEffect(() => {
        const trigger = indexesRef.current?.querySelector(
            '[data-bs-toggle="tooltip"]',
        );
        if (!trigger) return undefined;
        const tooltip = new window.bootstrap.Tooltip(trigger);
        return () => tooltip.dispose();
    }, [props.data.indexes]);

    function getStatusBadge(automation) {
        const keysBadge = automation.missingKeys ? (
            <span
                className="badge bg-warning text-dark ms-1"
                title={`No ${automation.missingKeys} API key configured in Settings; this automation cannot place orders.`}
            >
                NO API KEYS
            </span>
        ) : null;

        const statusBadge = automation.schedule ? (
            automation.isActive ? (
                <span className="badge bg-info">SCHEDULED</span>
            ) : (
                <span className="badge bg-danger">STOPPED</span>
            )
        ) : automation.isActive ? (
            <span className="badge bg-success">RUNNING</span>
        ) : (
            <span className="badge bg-danger">STOPPED</span>
        );

        return (
            <>
                {statusBadge}
                {keysBadge}
            </>
        );
    }

    function getIndexes() {
        if (!props.data.indexes) return '-';
        const indexes = props.data.indexes
            .split(',')
            .map(index => index.trim())
            .filter(Boolean);
        const visible = indexes.slice(0, MAX_VISIBLE_INDEXES);
        const hidden = indexes.slice(MAX_VISIBLE_INDEXES);
        return (
            <span ref={indexesRef}>
                {visible.map(index => (
                    <span className="badge bg-secondary me-1 mb-1" key={index}>
                        {index}
                    </span>
                ))}
                {hidden.length ? (
                    <>
                        <span className="me-1">...</span>
                        {/* the icon svg has pointer-events: none, so the
                            tooltip trigger must be the span around it */}
                        <span
                            role="button"
                            className="d-inline-flex align-items-center ms-1"
                            title={hidden.join(', ')}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                        >
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="icon icon-xs text-gray-800"
                            />
                        </span>
                    </>
                ) : (
                    <></>
                )}
            </span>
        );
    }

    function getEditModal() {
        if (!props.data || props.data.schedule) return '';
        return props.data &&
            props.data.actions &&
            props.data.actions.length &&
            props.data.actions[0].type === 'GRID'
            ? '#modalGrid'
            : '#modalAutomation';
    }

    return (
        <tr>
            <td>{props.data.symbol}</td>
            <td>{props.data.name}</td>
            <td>{getIndexes()}</td>
            <td>{getStatusBadge(props.data)}</td>
            <td>
                <button
                    id={'view' + props.data.id}
                    type="button"
                    className="btn btn-primary btn-xs ms-2"
                    title="View this Automation current data"
                    onClick={props.onViewClick}
                >
                    <FontAwesomeIcon
                        icon={faChartLine}
                        className="icon icon-xs fa-lg mx-1"
                    />
                </button>
                <button
                    id={'edit' + props.data.id}
                    type="button"
                    className="btn btn-secondary btn-xs ms-2"
                    title="Edit this Automation"
                    data-bs-toggle={props.data.schedule ? '' : 'modal'}
                    data-bs-target={getEditModal()}
                    onClick={props.onEditClick}
                >
                    <svg
                        className="icon icon-xs"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                </button>
                {!props.data.schedule ? (
                    <button
                        id={'copy' + props.data.id}
                        type="button"
                        className="btn btn-secondary btn-xs ms-2"
                        title="Duplicate this Automation"
                        data-bs-toggle="modal"
                        data-bs-target={getEditModal()}
                        onClick={props.onCopyClick}
                    >
                        <svg
                            className="icon icon-xs"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                    </button>
                ) : (
                    <></>
                )}
                {props.data.isActive ? (
                    <button
                        id={'stop' + props.data.id}
                        type="button"
                        className="btn btn-danger btn-xs ms-2"
                        title="Stop this Automation"
                        onClick={props.onStopClick}
                    >
                        <svg
                            className="icon icon-xs"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                ) : (
                    <></>
                )}
                {!props.data.isActive ? (
                    <button
                        id={'start' + props.data.id}
                        type="button"
                        className="btn btn-success btn-xs ms-2"
                        title="Start this Automation"
                        onClick={props.onStartClick}
                    >
                        <svg
                            className="icon icon-xs"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                ) : (
                    <></>
                )}
                {!props.data.isActive ? (
                    <button
                        id={'delete' + props.data.id}
                        type="button"
                        className="btn btn-danger btn-xs ms-2"
                        title="Delete this Automation"
                        onClick={props.onDeleteClick}
                    >
                        <svg
                            className="icon icon-xs"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                ) : (
                    <></>
                )}
            </td>
        </tr>
    );
}

export default AutomationRow;
